import { Creature, CreatureState, Position, EnvironmentalConditions } from './Creature';
import { Food } from './Food';

export interface WorldState {
  width: number;
  height: number;
  creatures: CreatureState[];
  food: Food[];
  environmentalConditions: EnvironmentalConditions;
  time: number;
}

export class World {
  private static nextFoodId = 0;
  private static readonly FOOD_PER_CREATURE = 2;
  private static readonly MAX_FOOD = 100;
  private static readonly PLANT_FOOD_ENERGY = 20;
  private static readonly MEAT_FOOD_ENERGY = 40;
  private static readonly FRUIT_FOOD_ENERGY = 30;

  private creatures: Creature[] = [];
  private food: Food[] = [];
  private time: number = 0;
  private environmentalConditions: EnvironmentalConditions = {
    temperature: 0.5,
    resources: 0.5,
    hazards: 0.1
  };

  constructor(
    private readonly width: number,
    private readonly height: number,
    private readonly maxInitialCreatures: number = 10
  ) {
    this.spawnInitialFood();
    
    // Initialize with creatures up to maxInitialCreatures
    for (let i = 0; i < this.maxInitialCreatures; i++) {
      this.addRandomCreature();
    }
  }

  public update(deltaTime: number): void {
    this.time += deltaTime;

    // Update environmental conditions
    this.updateEnvironmentalConditions();

    // Update all creatures
    this.creatures.forEach(creature => {
      creature.update(deltaTime, {
        width: this.width,
        height: this.height,
        environmentalConditions: this.environmentalConditions,
        food: this.food
      });
    });

    // Handle food consumption
    this.handleFoodConsumption();

    // Handle reproduction
    this.handleReproduction();

    // Clean up dead creatures
    this.cleanupDeadCreatures();

    // Randomly add food
    if (Math.random() < 0.01) {
      const position = {
        x: Math.random() * this.width,
        y: Math.random() * this.height
      };
      const type = Math.random() < 0.7 ? 'plant' : Math.random() < 0.5 ? 'meat' : 'fruit';
      this.placeFood(position, type);
    }
  }

  private updateEnvironmentalConditions(): void {
    // Temperature cycles between hot and cold (day/night)
    this.environmentalConditions.temperature = 
      0.5 + 0.3 * Math.sin(this.time / 10);

    // Resources fluctuate based on temperature and time
    this.environmentalConditions.resources = 
      0.5 + 0.3 * Math.sin(this.time / 20) +
      0.2 * Math.sin(this.time / 5);

    // Hazards increase during extreme temperatures
    const tempExtreme = Math.abs(this.environmentalConditions.temperature - 0.5);
    this.environmentalConditions.hazards = 
      Math.max(0, (tempExtreme - 0.2) * 2) +
      0.1 * Math.sin(this.time / 15);
  }

  private handleFoodConsumption(): void {
    this.creatures.forEach(creature => {
      const creaturePos = creature.getPosition();
      const consumptionRadius = 20;

      this.food = this.food.filter(food => {
        const distance = Math.sqrt(
          Math.pow(creaturePos.x - food.position.x, 2) +
          Math.pow(creaturePos.y - food.position.y, 2)
        );

        if (distance <= consumptionRadius) {
          creature.consumeFood(food.energy);
          return false;
        }
        return true;
      });
    });
  }

  private handleReproduction(): void {
    const newCreatures: Creature[] = [];
    const maxPopulation = Math.floor(50 * this.environmentalConditions.resources);

    if (this.creatures.length >= maxPopulation) return;

    this.creatures.forEach(creature => {
      if (!creature.canReproduce()) return;

      // Find nearby partner for sexual reproduction
      const partner = this.findReproductionPartner(creature);
      const child = creature.reproduce(partner);
      
      if (child) {
        // Apply environmental effects to offspring
        child.applyEnvironmentalEffects(this.environmentalConditions);
        newCreatures.push(child);
      }
    });

    this.creatures.push(...newCreatures);
  }

  private findReproductionPartner(creature: Creature): Creature | undefined {
    const pos = creature.getPosition();
    const reproductionRadius = 50;

    return this.creatures.find(other => {
      if (other === creature || !other.canReproduce()) return false;

      const otherPos = other.getPosition();
      const distance = Math.sqrt(
        Math.pow(pos.x - otherPos.x, 2) +
        Math.pow(pos.y - otherPos.y, 2)
      );

      return distance <= reproductionRadius;
    });
  }

  private addRandomCreature(): void {
    const randomDNA = this.generateRandomDNA();
    const position = this.getRandomPosition();
    
    const creature = new Creature(randomDNA, position);
    creature.applyEnvironmentalEffects(this.environmentalConditions);
    this.creatures.push(creature);
  }

  private generateRandomDNA(): string {
    const expandedWords = [
      // Nature
      "River", "Mountain", "Forest", "Sky", "Ocean", "Flower", "Stone", "Tree", "Sun", "Moon",
      "Desert", "Valley", "Cave", "Island", "Volcano", "Jungle", "Meadow", "Swamp", "Glacier", "Canyon",
      // Animals/Concepts
      "Wolf", "Eagle", "Serpent", "Spirit", "Ghost", "Power", "Speed", "Light", "Dark", "Fire", "Water", "Ice", "Storm", "Wind",
      "Lion", "Tiger", "Bear", "Shark", "Dragon", "Phoenix", "Shadow", "Energy", "Void", "Time", "Life", "Death",
      // Actions
      "Run", "Jump", "Fly", "Swim", "Hide", "Seek", "Attack", "Defend", "Grow", "Shrink", "Evolve", "Adapt", "Transform",
      "Strike", "Dash", "Guard", "Heal", "Rest", "Build", "Destroy", "Explore", "Create", "Observe",
      // Descriptions
      "Fast", "Slow", "Big", "Small", "Red", "Blue", "Green", "Bright", "Dark", "Strong", "Weak", "Old", "New", "Ancient", "Young",
      "Sharp", "Dull", "Quiet", "Loud", "Heavy", "Light", "Brave", "Calm", "Wild", "Free", "Wise", "Silent",
      // Misc
      "Magic", "Song", "Dream", "Star", "Begin", "End", "Echo", "Pulse", "Rune", "Glyph", "Whisper", "Cry"
    ];
    const punctuationMarks = ['.', '!', '?', ',', ';'];

    const wordCount = Math.floor(Math.random() * 4) + 2; // 2 to 5 words
    let dnaParts = [];

    for (let i = 0; i < wordCount; i++) {
      let word = expandedWords[Math.floor(Math.random() * expandedWords.length)];
      // Randomly capitalize the word or just the first letter
      if (Math.random() < 0.3) { // 30% chance to capitalize whole word
        word = word.toUpperCase();
      } else if (Math.random() < 0.5) { // 50% chance (of the remaining 70%, so 35% of total) to capitalize first letter
        word = word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      } else {
        word = word.toLowerCase(); // Ensure some words are lowercase (remaining 35% of total)
      }
      dnaParts.push(word);
    }

    let dnaString = dnaParts.join(' ');

    // Randomly add a punctuation mark at the end
    if (Math.random() < 0.5) { // 50% chance to add punctuation
      dnaString += punctuationMarks[Math.floor(Math.random() * punctuationMarks.length)];
    }

    return dnaString;
  }

  private getRandomPosition(): Position {
    return {
      x: Math.random() * this.width,
      y: Math.random() * this.height
    };
  }

  public getState(): WorldState {
    return {
      creatures: this.creatures.map(creature => creature.getState()),
      food: [...this.food],
      width: this.width,
      height: this.height,
      time: this.time,
      environmentalConditions: { ...this.environmentalConditions }
    };
  }

  public addCreature(dna: string): void {
    const position = {
      x: Math.random() * this.width,
      y: Math.random() * this.height
    };
    this.creatures.push(new Creature(dna, position));
  }

  public placeFood(position: Position, type: Food['type'] = 'plant', size: number = 1, energyInput?: number): void {
    // Validate position is within world bounds
    if (position.x < 0 || position.x > this.width || position.y < 0 || position.y > this.height) {
      console.warn('Food position is outside world bounds');
      return;
    }

    let determinedEnergy: number;
    if (energyInput !== undefined) {
      determinedEnergy = energyInput;
    } else {
      switch (type) {
        case 'plant':
          determinedEnergy = World.PLANT_FOOD_ENERGY;
          break;
        case 'meat':
          determinedEnergy = World.MEAT_FOOD_ENERGY;
          break;
        case 'fruit':
          determinedEnergy = World.FRUIT_FOOD_ENERGY;
          break;
        default: // Should not happen with strict types, but good for safety
          determinedEnergy = World.PLANT_FOOD_ENERGY;
      }
    }

    // Determine color based on food type
    const color = this.getFoodColor(type);

    // Create and add the food
    const newFood: Food = {
      id: `food-${World.nextFoodId++}`,
      position,
      type,
      size,
      energy: determinedEnergy, // Use the determined energy
      color
    };

    this.food.push(newFood);
  }

  private getFoodColor(type: Food['type']): string {
    switch (type) {
      case 'plant':
        return '#2ecc71'; // Green
      case 'meat':
        return '#e74c3c'; // Red
      case 'fruit':
        return '#f1c40f'; // Yellow
      default:
        return '#95a5a6'; // Gray
    }
  }

  public addFood(position: Position, type: Food['type'] = 'plant'): void {
    let determinedEnergy: number;
    switch (type) {
      case 'plant':
        determinedEnergy = World.PLANT_FOOD_ENERGY;
        break;
      case 'meat':
        determinedEnergy = World.MEAT_FOOD_ENERGY;
        break;
      case 'fruit':
        determinedEnergy = World.FRUIT_FOOD_ENERGY;
        break;
      default: // Should not happen
        determinedEnergy = World.PLANT_FOOD_ENERGY;
    }

    const newFood: Food = {
      id: `food-${World.nextFoodId++}`,
      position,
      type,
      size: 1, // Keep default size for this method
      energy: determinedEnergy, // Use the determined energy
      color: this.getFoodColor(type)
    };
    this.food.push(newFood);
  }

  private spawnInitialFood(): void {
    // Start with just enough food for initial creatures
    const initialFoodCount = Math.min(
      this.maxInitialCreatures * World.FOOD_PER_CREATURE,
      World.MAX_FOOD
    );
    for (let i = 0; i < initialFoodCount; i++) {
      const position = {
        x: Math.random() * this.width,
        y: Math.random() * this.height
      };
      this.placeFood(position, 'plant', 1, World.PLANT_FOOD_ENERGY);
    }
  }

  private cleanupDeadCreatures(): void {
    this.creatures = this.creatures.filter(creature => creature.getState().isAlive);
  }
} 